/* eslint-disable @typescript-eslint/consistent-type-assertions */
import {
  TBrowserApiData,
  TEventDataInput,
  TEventInputData,
} from '@/core/constants/event_types/additional_types';
import { TCartData } from '@/core/constants/event_types/extended_mable_event';
import {
  CustomPixelStdEventName,
  ShpyCpxHandlerInput,
} from '@/sources/bundles/wtag-shpfy-cpx/shopify_custom_pixel_types/cpx_payload_types';

import { addCpxEventMetadata } from '../utils/event_metadata';
import { GenericShpyCpxTrigger } from './generic_shpy_cpx_event_triggers';

const buildEventInputData = async (
  cpx_input_payload: ShpyCpxHandlerInput,
  cpxEventName: CustomPixelStdEventName,
): Promise<TEventInputData> => {
  const products = GenericShpyCpxTrigger.getProductsForCpx(cpx_input_payload);
  const cdas = GenericShpyCpxTrigger.getCustomerData(cpx_input_payload);
  const browserData: TBrowserApiData =
    GenericShpyCpxTrigger.getCustomPixelBrowserData(cpx_input_payload);
  const cart: TCartData = GenericShpyCpxTrigger.getCartData(cpx_input_payload);
  const price_fields = GenericShpyCpxTrigger.getPriceFields({
    deps: cpx_input_payload,
  });

  let eventData: TEventDataInput | undefined;

  if (cpxEventName === CustomPixelStdEventName.PAGE_VIEWED) {
    return {
      browserData,
      cdas,
      eventData: addCpxEventMetadata(undefined, cpx_input_payload),
    };
  }

  if (cpxEventName === CustomPixelStdEventName.PRODUCT_ADDED_TO_CART) {
    eventData = {
      act_product: products[0],
      cv_data: {
        cv: price_fields.total_discounted_price,
        discount: price_fields.total_discounts,
        org_cv: price_fields.total_discounted_price,
        shipping: price_fields.total_shipping_price,
        tax: price_fields.total_tax,
        total_line_items_price: price_fields.total_line_items_price,
      },
      qty_act_product: cpx_input_payload?.event?.data?.cartLine?.quantity || 1,
    } as TEventDataInput;
  }

  if (cpxEventName === CustomPixelStdEventName.PRODUCT_VIEWED) {
    eventData = {
      act_product: products[0],
      cv_data: {
        cv: price_fields.total_discounted_price,
        discount: price_fields.total_discounts,
        org_cv: price_fields.total_discounted_price,
        shipping: price_fields.total_shipping_price,
        tax: price_fields.total_tax,
        total_line_items_price: price_fields.total_line_items_price,
      },
    } as TEventDataInput;
  }

  if (
    cpxEventName === CustomPixelStdEventName.CHECKOUT_STARTED ||
    cpxEventName === CustomPixelStdEventName.CHECKOUT_ADDRESS_INFO_SUBMITTED ||
    cpxEventName === CustomPixelStdEventName.CHECKOUT_SHIPPING_INFO_SUBMITTED
  ) {
    eventData = {
      cart,
      cv_data: {
        cv: price_fields.total_discounted_price,
        discount: price_fields.total_discounts,
        line_items_price: price_fields.total_line_items_price,
        org_cv: price_fields.total_discounted_price,
        shipping: price_fields.total_shipping_price,
        tax: price_fields.total_tax,
      },
    } as TEventDataInput;
  }

  if (cpxEventName === CustomPixelStdEventName.PAYMENT_INFO_SUBMITTED) {
    eventData = {
      cart,
      cv_data: {
        cv: price_fields.total_discounted_price,
        discount: price_fields.total_discounts,
        line_items_price: price_fields.total_line_items_price,
        org_cv: price_fields.total_discounted_price,
        shipping: price_fields.total_shipping_price,
        tax: price_fields.total_tax,
      },
      pay_methods:
        GenericShpyCpxTrigger.getPaymentMethod(cpx_input_payload) || '',
    } as TEventDataInput;
  }

  if (cpxEventName === CustomPixelStdEventName.CHECKOUT_COMPLETED) {
    eventData = {
      cart,
      cv_data: {
        cv: price_fields.total_discounted_price,
        discount: price_fields.total_discounts,
        line_items_price: price_fields.total_line_items_price,
        org_cv: price_fields.total_discounted_price,
        shipping: price_fields.total_shipping_price,
        tax: price_fields.total_tax,
      },
      oid: cpx_input_payload?.event?.data?.checkout?.order.id || '',
      onb: '',
      pay_methods:
        GenericShpyCpxTrigger.getPaymentMethod(cpx_input_payload) || '',
    } as Partial<TEventDataInput>;
  }

  if (!eventData) {
    eventData = {
      cart,
      cv_data: {
        cv: price_fields.total_discounted_price,
        discount: price_fields.total_discounts,
        line_items_price: price_fields.total_line_items_price,
        org_cv: price_fields.total_discounted_price,
        shipping: price_fields.total_shipping_price,
        tax: price_fields.total_tax,
      },
      oid: cpx_input_payload?.event?.data?.checkout?.order.id || '',
      onb: '',
      pay_methods:
        GenericShpyCpxTrigger.getPaymentMethod(cpx_input_payload) || '',
    } as Partial<TEventDataInput>;
  }

  return {
    browserData,
    cdas,
    eventData: addCpxEventMetadata(eventData, cpx_input_payload),
  };
};

export class ShpyCpxFallBackTrigger extends GenericShpyCpxTrigger {
  public static async transformToWtagInput(
    cpx_input_payload: ShpyCpxHandlerInput,
  ): Promise<TEventInputData> {
    return buildEventInputData(
      cpx_input_payload,
      CustomPixelStdEventName.UNKNOWN_EVENT,
    );
  }
}

export class ShpyCpxAddToCartTrigger extends GenericShpyCpxTrigger {
  public static async transformToWtagInput(
    cpx_input_payload: ShpyCpxHandlerInput,
  ): Promise<TEventInputData> {
    return buildEventInputData(
      cpx_input_payload,
      CustomPixelStdEventName.PRODUCT_ADDED_TO_CART,
    );
  }
}

export class CpxPurchaseTrigger extends GenericShpyCpxTrigger {
  public static async transformToWtagInput(
    cpx_input_payload: ShpyCpxHandlerInput,
  ): Promise<TEventInputData> {
    return buildEventInputData(
      cpx_input_payload,
      CustomPixelStdEventName.CHECKOUT_COMPLETED,
    );
  }
}

export class ShpyCpxCheckoutAddressInfoSubmittedTrigger extends GenericShpyCpxTrigger {
  public static async transformToWtagInput(
    cpx_input_payload: ShpyCpxHandlerInput,
  ): Promise<TEventInputData> {
    return buildEventInputData(
      cpx_input_payload,
      CustomPixelStdEventName.CHECKOUT_ADDRESS_INFO_SUBMITTED,
    );
  }
}

export class ShpyCpxCheckoutShippingInfoSubmittedTrigger extends GenericShpyCpxTrigger {
  public static async transformToWtagInput(
    cpx_input_payload: ShpyCpxHandlerInput,
  ): Promise<TEventInputData> {
    return buildEventInputData(
      cpx_input_payload,
      CustomPixelStdEventName.CHECKOUT_SHIPPING_INFO_SUBMITTED,
    );
  }
}

export class ShpyCpxCheckoutStartedTrigger extends GenericShpyCpxTrigger {
  public static async transformToWtagInput(
    cpx_input_payload: ShpyCpxHandlerInput,
  ): Promise<TEventInputData> {
    return buildEventInputData(
      cpx_input_payload,
      CustomPixelStdEventName.CHECKOUT_STARTED,
    );
  }
}

export class ShpyCpxCollectionViewedTrigger extends GenericShpyCpxTrigger {
  public static async transformToWtagInput(
    cpx_input_payload: ShpyCpxHandlerInput,
  ): Promise<TEventInputData> {
    return buildEventInputData(
      cpx_input_payload,
      CustomPixelStdEventName.COLLECTION_VIEWED,
    );
  }
}

export class ShpyCpxPageViewTrigger extends GenericShpyCpxTrigger {
  public static async transformToWtagInput(
    cpx_input_payload: ShpyCpxHandlerInput,
  ): Promise<TEventInputData> {
    return buildEventInputData(
      cpx_input_payload,
      CustomPixelStdEventName.PAGE_VIEWED,
    );
  }
}

export class ShpyCpxPaymentInfoSubmittedTrigger extends GenericShpyCpxTrigger {
  public static async transformToWtagInput(
    cpx_input_payload: ShpyCpxHandlerInput,
  ): Promise<TEventInputData> {
    return buildEventInputData(
      cpx_input_payload,
      CustomPixelStdEventName.PAYMENT_INFO_SUBMITTED,
    );
  }
}

export class ShpyCpxProductRemovedFromCartTrigger extends GenericShpyCpxTrigger {
  public static async transformToWtagInput(
    cpx_input_payload: ShpyCpxHandlerInput,
  ): Promise<TEventInputData> {
    return buildEventInputData(
      cpx_input_payload,
      CustomPixelStdEventName.PRODUCT_REMOVED_FROM_CART,
    );
  }
}

export class ShpyCpxProductViewedTrigger extends GenericShpyCpxTrigger {
  public static async transformToWtagInput(
    cpx_input_payload: ShpyCpxHandlerInput,
  ): Promise<TEventInputData> {
    return buildEventInputData(
      cpx_input_payload,
      CustomPixelStdEventName.PRODUCT_VIEWED,
    );
  }
}
export class ShpyCpxCartViewedTrigger extends GenericShpyCpxTrigger {
  public static async transformToWtagInput(
    cpx_input_payload: ShpyCpxHandlerInput,
  ): Promise<TEventInputData> {
    return buildEventInputData(
      cpx_input_payload,
      CustomPixelStdEventName.UNKNOWN_EVENT,
    );
  }
}
